import React, { useCallback, useContext, useState } from 'react';
import { z } from 'zod';

import Form from 'components/shared/Form';

import { ItemsContext } from 'components/shared/context/ItemsContextProvider';
import { ToastContext } from 'components/shared/context/ToastContextProvider';
import { UserContext } from 'components/shared/context/UserContextProvider';

import ValidatedBaseInput from 'components/shared/inputs/ValidatedBaseInput';

import BaseModal from 'components/shared/modals/BaseModal';

import sansanTech from 'services/sansantech';

interface AddItemModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddItemModal: React.FC<AddItemModalProps> = ({ isOpen, setIsOpen }) => {
  const { userState } = useContext(UserContext);
  const { toastDispatch } = useContext(ToastContext);
  const { setItems } = useContext(ItemsContext);

  const [submitting, setSubmitting] = useState(false);

  const [itemNameValue, setItemNameValue] = useState<string>('');
  const [isItemNameValueValid, setIsItemNameValueValid] =
    useState<boolean>(false);

  const reset = useCallback(() => {
    setItemNameValue('');
    setIsItemNameValueValid(false);
  }, []);

  const onSubmit = useCallback(() => {
    setSubmitting(true);
    if (userState.token) {
      sansanTech
        .createItem(userState.token, { name: itemNameValue })
        .then((response) => {
          console.log(response);
          setItems?.((currentItems) => {
            const newItem = response.data;
            return [newItem, ...currentItems];
          });
          setSubmitting(false);
          toastDispatch?.({
            type: 'SUCCESS',
            message: 'Added item',
          });
          setIsOpen(false);
          reset();
        })
        .catch((error) => {
          console.log(error);
          setSubmitting(false);
          toastDispatch?.({
            type: 'ERROR',
            message: 'Could not add item',
          });
        });
    } else {
      setSubmitting(false);
      toastDispatch?.({ type: 'ERROR', message: 'No permission to do this' });
    }
  }, [
    itemNameValue,
    reset,
    toastDispatch,
    userState.token,
    setIsOpen,
    setItems,
  ]);

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Add item"
      onClose={reset}
    >
      <Form
        header="Add item"
        onSubmit={onSubmit}
        submitting={submitting}
        disabled={!isItemNameValueValid}
        submitButtonText="Add item"
        submitButtonWidth="110px"
      >
        <ValidatedBaseInput
          label="item name"
          type="text"
          value={itemNameValue}
          setValue={setItemNameValue}
          setIsValid={setIsItemNameValueValid}
          validators={z.string().min(1, { message: 'This field is required' })}
        />
      </Form>
    </BaseModal>
  );
};

export default AddItemModal;
